import { z } from 'zod';

/**
 * Where an entitlement came from.
 *
 * The column exists so the model is **store-agnostic** (N14): gating asks
 * whether a member is entitled, never which channel paid for it. Web payments
 * (the roadmap tail) become a fifth value and nothing that gates changes.
 *
 * - `app_store` / `play_store` — an IAP subscription, synced by the RevenueCat
 *   webhook. The store owns the truth; the platform mirrors it.
 * - `direct` — a staff-recorded off-platform payment, for markets where Apple
 *   and Google billing are unavailable.
 * - `promo` — an admin grant with no money behind it: App Review test accounts,
 *   internal testers, comps.
 */
export const ENTITLEMENT_STORES = ['app_store', 'play_store', 'direct', 'promo'] as const;

export const EntitlementStoreSchema = z.enum(ENTITLEMENT_STORES);

export type EntitlementStore = (typeof ENTITLEMENT_STORES)[number];

const STORE_SET: ReadonlySet<string> = new Set(ENTITLEMENT_STORES);

export function isEntitlementStore(value: string): value is EntitlementStore {
  return STORE_SET.has(value);
}

/**
 * The stores whose entitlements this platform writes and expires on its own
 * clock. IAP entitlements are **never** expired by the sweep — the store is the
 * authority and says so through the webhook, so ageing one out locally would
 * revoke access a member is still paying for during any webhook outage.
 */
export const SELF_MANAGED_ENTITLEMENT_STORES: readonly EntitlementStore[] = ['direct', 'promo'];
